import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    BorderStyle,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE } from "@/utils/wordGenerator/common.js";

export const createLPTemp1 = async (lpData) => {

    const HEADER_HEIGHT = 1200;
    const FOOTER_HEIGHT = 1200;
    const SPACER_HEIGHT = 2500;
    const MIDDLE_HEIGHT = 8950;
    const LOGO_WIDTH = 3200;

    const style = lpData.style || {};
    const headerColor = fixColor(style.colorScheme?.header || "#f0f0f0");
    const bodyColor = fixColor(style.colorScheme?.body || "#f0f0f0");
    const footerColor = fixColor(style.colorScheme?.footer || "#f0f0f0");

    // Helper to extract style props
    const getStyleProps = (styleObj, defaultSize = 12) => ({
        font: styleObj?.font || "Arial",
        size: (styleObj?.size || defaultSize) * SCALE,
        bold: styleObj?.bold,
        italics: styleObj?.italic,
        underline: styleObj?.underline ? {} : undefined, // docx underline object or type
        color: fixColor(styleObj?.color || "#000000")
    });

    const labelStyle = getStyleProps(style.label, 24);
    const textStyle = getStyleProps(style.text, 12);

    // 1. Header Logo (Top Left Cell)
    let logoRun = null;
    if (lpData.logo) {
        const logoData = await fetchImageWithDimensions(lpData.logo);
        if (logoData) {
            logoRun = fitImage(logoData, LOGO_WIDTH, 1100, "contain");
        }
    }

    // 2. Middle Section Logo
    let centerLogoRun = null;
    if (lpData.logo) {
        const logoData = await fetchImageWithDimensions(lpData.logo);
        if (logoData) {
            centerLogoRun = fitImage(logoData, 3500, 3500, "contain");
        }
    }

    // 1. Header Table
    const headerTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: HEADER_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: 27, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        verticalAlign: VerticalAlign.CENTER,
                        children: logoRun
                            ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [logoRun] })]
                            : [new Paragraph("")]
                    }),
                    new TableCell({
                        width: { size: 3, type: WidthType.PERCENTAGE },
                        shading: { fill: "FFFFFF" },
                        children: []
                    }),
                    new TableCell({
                        width: { size: 70, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        children: []
                    })
                ]
            })
        ]
    });

    // 2. Middle Section Table
    const middleSectionTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: MIDDLE_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        shading: { fill: bodyColor },
                        verticalAlign: VerticalAlign.CENTER,
                        children: [
                            // Center Logo
                            centerLogoRun ? new Paragraph({ alignment: AlignmentType.CENTER, children: [centerLogoRun], spacing: { after: 400 } }) : new Paragraph(""),

                            // Name
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 100 },
                                children: [new TextRun({ text: lpData.name || "N/A", ...labelStyle })]
                            }),

                            // Address
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [new TextRun({ text: lpData.address || "N/A", ...textStyle })]
                            }),

                            // City, State
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [new TextRun({ text: `${lpData.city || 'N/A'}, ${lpData.stateProvince || 'N/A'}`, ...textStyle })]
                            }),

                            // Contact
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [new TextRun({ text: lpData.contactNo || "N/A", ...textStyle })]
                            }),

                            // Email
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [new TextRun({ text: lpData.email || "N/A", ...textStyle })]
                            }),
                        ]
                    })
                ]
            })
        ]
    });

    // 3. Footer Table
    const footerTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: FOOTER_HEIGHT, rule: HeightRule.EXACT },
                children: [new TableCell({ shading: { fill: footerColor }, children: [] })]
            })
        ]
    });

    return {
        properties: {
            page: {
                margin: { top: 0, right: 0, bottom: 0, left: 0 }
            }
        },
        children: [
            headerTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            middleSectionTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            footerTable
        ]
    };
};

